import React, { useCallback, useState } from 'react';
import { Text, Icon, Pills, Tooltip } from 'barte-design-system';
import { LockIcon, X } from '@phosphor-icons/react';
import './bolsao.css';

/**
 * Textos e componentes de ajuda do bolsão, compartilhados entre as telas de
 * detalhe do lojista. Ficam aqui para que as duas expliquem a mesma regra com
 * as mesmas palavras — o teste com o Arthur mostrou que a dúvida é a regra,
 * não o layout.
 */

// ─── Types ───────────────────────────────────────────────────────────────────

/** Estados do bolsão previstos no UX_BRIEF. */
export type BolsaoStatus = 'zerado' | 'retendo' | 'teto';

export type PillState = 'default' | 'success' | 'alert' | 'info';

// ─── Textos ──────────────────────────────────────────────────────────────────

/**
 * Formulação única de quando a retenção acontece. O tooltip do cadeado, a
 * ajuda da pill e o bloco "Como funciona" derivam todos daqui, para não
 * descreverem a mesma regra de três jeitos diferentes.
 */
export const BOLSAO_FREQUENCIA =
  'A retenção acontece uma vez por dia, sobre o valor líquido que será executado no dia';

/** Texto do tooltip do cadeado. Acompanha o teto, que pode não existir. */
export function bolsaoAjuda(teto: number | null): string {
  const limite =
    teto === null
      ? 'e continua enquanto houver liquidação, porque não há teto configurado'
      : 'até o teto configurado';
  return `Garantia retida do saldo do lojista. ${BOLSAO_FREQUENCIA}, ${limite}.`;
}

/** Descrição das pills de saldo, no tooltip. */
export const SALDO_LIBERADO_AJUDA = 'Valores já liquidados.';
export const SALDO_A_LIBERAR_AJUDA = 'Parcelas que estão para liquidar.';

/**
 * Rótulo, cor e ajuda de cada status no mesmo lugar: um status novo entra uma
 * vez só, em vez de precisar ser lembrado em dois records paralelos.
 */
export const BOLSAO_STATUS: Record<
  BolsaoStatus,
  { label: string; state: PillState; ajuda: string }
> = {
  zerado: {
    label: 'Sem retenção ainda',
    state: 'default',
    ajuda: 'Ainda sem retenção.',
  },
  retendo: {
    label: 'Retendo',
    state: 'info',
    ajuda: `${BOLSAO_FREQUENCIA}.`,
  },
  teto: {
    label: 'Teto atingido',
    state: 'success',
    ajuda: 'Teto atingido, sem novas retenções.',
  },
};

// ─── Componentes ─────────────────────────────────────────────────────────────

interface BolsaoAjudaIconProps {
  teto: number | null;
  /** Se a pessoa já fechou o tooltip. Mora na tela, não aqui: as abas são
   *  render condicional e este componente desmonta ao trocar de aba. */
  fechado: boolean;
  onFechar: () => void;
}

/**
 * Cadeado do card do bolsão, com a explicação da regra.
 * Abre sozinho na primeira visita e fica aberto até a pessoa fechar no X.
 * Depois de fechado, volta a ser um tooltip normal, de hover.
 * [A DEFINIR] guardar esse "já vi" por usuário, para não abrir toda vez.
 */
export function BolsaoAjudaIcon({ teto, fechado, onFechar }: BolsaoAjudaIconProps) {
  return (
    <Tooltip
      title="Bolsão"
      placement="top"
      {...(fechado ? {} : { isOpen: true })}
      label={
        <span className="bolsao-ajuda">
          {bolsaoAjuda(teto)}
          {!fechado && (
            <button type="button" className="bolsao-ajuda-fechar" aria-label="Fechar" onClick={onFechar}>
              <Icon icon={X} size={12} />
            </button>
          )}
        </span>
      }
    >
      {/* Botão, e não span: o gatilho precisa ser focável por teclado, senão
          quem navega sem mouse nunca chega no texto. */}
      <button type="button" className="bolsao-help-lock" aria-label="O que é o bolsão">
        <Icon icon={LockIcon} size={16} />
      </button>
    </Tooltip>
  );
}

/**
 * Entrega o cadeado pronto para o `icon` do CardBalance.Info, com o estado do
 * "já fechei" junto. O estado precisa viver na tela, e não dentro do ícone:
 * as abas são render condicional, então o card desmonta ao trocar de aba e o
 * tooltip voltaria a abrir à força. Como as duas telas precisavam da mesma
 * fiação, ela fica aqui em vez de duplicada nas duas.
 */
export function useBolsaoAjudaIcon(teto: number | null): React.ElementType {
  const [fechado, setFechado] = useState(false);

  return useCallback(
    () => <BolsaoAjudaIcon teto={teto} fechado={fechado} onFechar={() => setFechado(true)} />,
    [teto, fechado]
  );
}

/**
 * Pill de status com a ajuda no tooltip. Existia repetida em quatro pontos —
 * as duas telas, na faixa de saldo e na aba Garantias — variando só o size.
 */
export function BolsaoStatusPill({
  status,
  size,
}: {
  status: BolsaoStatus;
  size: 'sm' | 'base';
}) {
  const { label, state, ajuda } = BOLSAO_STATUS[status];

  return (
    <Tooltip label={ajuda} placement="top">
      <span className="bolsao-pill-tooltip">
        <Pills label={label} state={state} variant="light" size={size} />
      </span>
    </Tooltip>
  );
}

/** Bloco "Como funciona" da aba Garantias. */
export function BolsaoComoFunciona({ teto }: { teto: number | null }) {
  return (
    <div className="bolsao-explicacao">
      <Text variant="label-lg" style={{ fontWeight: 600 }}>
        Como funciona
      </Text>
      <ul>
        <li>
          <Text variant="body-sm">{BOLSAO_FREQUENCIA}.</Text>
        </li>
        <li>
          <Text variant="body-sm">
            {teto === null
              ? 'O percentual configurado é aplicado enquanto houver liquidação. Não há teto configurado.'
              : 'O percentual configurado é aplicado até o bolsão chegar no teto.'}
          </Text>
        </li>
        <li>
          <Text variant="body-sm">Estorno e chargeback não são pagos com o bolsão.</Text>
        </li>
        <li>
          <Text variant="body-sm">
            Para liberar, aportar ou devolver esse valor entre em contato com o atendimento da
            Barte.
          </Text>
        </li>
      </ul>
    </div>
  );
}
